import { Injectable } from '@angular/core';
import { environment } from "../../../enviroments/environment";
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { UserPermissionsService } from '../user-permissions.service';
import { UserInfoService } from '../user-info.service';
import { UserEmployeeLinkService } from '../user-employee-link.service';

@Injectable({
  providedIn: 'root'
})
export class CognitoServiceService {
  cognitoUser: any;

  // BehaviorSubject para manejar el estado de loading
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();

  // BehaviorSubject para manejar el estado de autenticación
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$: Observable<boolean> = this.isAuthenticatedSubject.asObservable();

  // Flag para evitar múltiples intentos de refresh simultáneos
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(
    private router: Router,
    private userPermissionsService: UserPermissionsService,
    private userInfoService: UserInfoService,
    private userEmployeeLinkService: UserEmployeeLinkService
  ) { }

  // Método público para controlar el estado de loading
  setLoading(isLoading: boolean): void {
    this.isLoadingSubject.next(isLoading);
  }

  // Guardar mensaje de error para mostrarlo después del logout
  private setLogoutMessage(message: string): void {
    sessionStorage.setItem('logout_message', message);
  }

  // Obtener y limpiar mensaje de logout
  public getAndClearLogoutMessage(): string | null {
    const message = sessionStorage.getItem('logout_message');
    if (message) {
      sessionStorage.removeItem('logout_message');
    }
    return message;
  }

  /**
   * Decodificar token JWT para obtener información del usuario
   */
  private decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decodificando token:', error);
      return null;
    }
  }

  /**
   * Verificar y vincular usuario con empleado por email
   */
  private async verifyAndLinkEmployee(idToken: string): Promise<boolean> {
    try {
      const decoded = this.decodeToken(idToken);
      if (!decoded) {
        console.error('No se pudo decodificar el token');
        return false;
      }

      const userId = decoded['sub'];
      const userEmail = decoded['email'];

      if (!userId || !userEmail) {
        console.error('Token no contiene userId o email');
        return false;
      }

      console.log(`Verificando empleado para email: ${userEmail}`);

      // Intentar vincular usuario con empleado
      const response = await firstValueFrom(
        this.userEmployeeLinkService.linkUserToEmployee({
          IdUser: userId,
          Email: userEmail,
          IdCompany: 1 // Por defecto empresa 1, ajustar según necesidad
        })
      );

      if (response.success && response.data) {
        console.log('Usuario vinculado exitosamente con empleado:', response.data);
        // Guardar información del empleado vinculado
        localStorage.setItem('linkedEmployee', JSON.stringify(response.data));
        return true;
      } else {
        console.warn('No se encontró empleado para el email:', userEmail);
        return false;
      }
    } catch (error: any) {
      // Si es error 404, significa que no existe empleado con ese email
      if (error?.status === 404) {
        console.warn('No existe empleado con ese correo corporativo');
        return false;
      }
      console.error('Error verificando empleado:', error);
      return false;
    }
  }

  exchangeCodeForTokens(code: string): Promise<boolean> {
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: environment.cognitoAppClientId,
      code: code,
      redirect_uri: environment.cognitoRedirectUri,
    });

    return fetch(`${environment.cognitoUserPoolDomain}/oauth2/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: body.toString()
    })
      .then(res => {
        if (!res.ok) {
          console.error('Error al obtener tokens - Status:', res.status, res.statusText);
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        console.log('Tokens obtenidos exitosamente del servidor Cognito');
        return res.json();
      })
      .then(async tokens => {
        console.log('Almacenando tokens...');
        this.storeTokens(tokens);

        // Actualizar estado de autenticación
        this.isAuthenticatedSubject.next(true);

        // Cargar información del usuario desde el token
        this.userInfoService.loadUserInfo();

        // Verificar y vincular usuario con empleado
        console.log('Verificando vinculación usuario-empleado...');
        const employeeLinked = await this.verifyAndLinkEmployee(tokens.id_token);

        if (!employeeLinked) {
          console.warn('Usuario no tiene empleado asociado');
          this.isLoadingSubject.next(false);
          this.setLogoutMessage('Solicite accesos al administrador. No se encontró un empleado registrado con su correo corporativo.');
          await this.logOut();
          return false;
        }

        // Cargar permisos del usuario
        console.log('Cargando permisos del usuario...');
        const permissions = await this.userPermissionsService.loadUserPermissions().toPromise();

        // Limpiar URL de parámetros OAuth
        window.history.replaceState({}, document.title, '/');

        // Verificar si el usuario no tiene permisos
        if (!permissions || permissions.length === 0) {
          console.warn('Usuario sin permisos asignados');
          this.isLoadingSubject.next(false);

          // Guardar mensaje para mostrar después del redirect
          this.setLogoutMessage('Usuario no autorizado. No tienes permisos asignados para acceder al sistema.');
          await this.logOut();
          return false;
        }

        this.isLoadingSubject.next(false);
        return true;
      })
      .catch(error => {
        console.error('Error en proceso de autenticación:', {
          message: error?.message,
          status: error?.status,
          errorDetails: error
        });
        this.isLoadingSubject.next(false);

        // Guardar mensaje para mostrar después del redirect
        this.setLogoutMessage(`Error de autenticación: ${error?.message || 'Error desconocido'}. Por favor, intenta nuevamente.`);
        this.isAuthenticatedSubject.next(false);
        this.logOut();
        return false;
      });
  }

  storeTokens(tokens: any) {
    if (!tokens || !tokens.access_token || !tokens.id_token) {
      console.error('Tokens inválidos recibidos');
      throw new Error('Tokens inválidos');
    }

    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('id_token', tokens.id_token);
    if (tokens.refresh_token) {
      localStorage.setItem('refresh_token', tokens.refresh_token);
    }

    // Guardar timestamp de cuando se obtuvieron los tokens
    localStorage.setItem('tokens_timestamp', Date.now().toString());
  }

  /**
   * Verificar si el token está expirado
   */
  private isTokenExpired(token: string): boolean {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const decoded = JSON.parse(jsonPayload);

      // exp está en segundos, Date.now() en milisegundos
      const expirationTime = decoded.exp * 1000;
      const currentTime = Date.now();

      // Considerar expirado si faltan menos de 5 minutos
      const bufferTime = 5 * 60 * 1000; // 5 minutos en milisegundos
      return currentTime >= (expirationTime - bufferTime);
    } catch (error) {
      console.error('Error verificando expiración del token:', error);
      return true; // Si hay error, asumir que está expirado
    }
  }

  /**
   * Refrescar tokens usando el refresh_token
   */
  async refreshTokens(): Promise<boolean> {
    // Si ya hay un refresh en progreso, esperar a que termine
    if (this.isRefreshing && this.refreshPromise) {
      console.log('Ya hay un refresh en progreso, esperando...');
      return this.refreshPromise;
    }

    const refreshToken = localStorage.getItem('refresh_token');

    if (!refreshToken) {
      console.warn('No hay refresh_token disponible');
      return false;
    }

    console.log('Intentando refrescar tokens...');
    this.isRefreshing = true;

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: environment.cognitoAppClientId,
      refresh_token: refreshToken
    });

    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${environment.cognitoUserPoolDomain}/oauth2/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: body.toString()
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error al refrescar tokens - Status:', response.status, response.statusText, errorText);

          // Si el refresh token también expiró (401), hacer logout
          if (response.status === 401 || response.status === 400) {
            console.warn('Refresh token inválido o expirado, cerrando sesión...');
            this.setLogoutMessage('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
            await this.logOut();
            return false;
          }

          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const tokens = await response.json();
        console.log('Tokens refrescados exitosamente');

        // Guardar nuevos tokens (el refresh_token puede o no venir en la respuesta)
        this.storeTokens({
          access_token: tokens.access_token,
          id_token: tokens.id_token,
          refresh_token: tokens.refresh_token || refreshToken // Mantener el anterior si no viene uno nuevo
        });

        // Actualizar información del usuario con el nuevo token
        this.userInfoService.loadUserInfo();

        return true;
      } catch (error) {
        console.error('Error refrescando tokens:', error);
        return false;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Obtener token válido (refrescar si es necesario)
   */
  async getValidToken(): Promise<string | null> {
    const idToken = localStorage.getItem('id_token');

    if (!idToken) {
      console.warn('No hay token de autenticación');
      return null;
    }

    // Verificar si el token está expirado
    if (this.isTokenExpired(idToken)) {
      console.log('Token expirado, intentando refrescar...');
      const refreshed = await this.refreshTokens();

      if (refreshed) {
        // Retornar el nuevo token
        return localStorage.getItem('id_token');
      } else {
        // No se pudo refrescar
        return null;
      }
    }

    return idToken;
  }

  // Procesar callback OAuth con el code de autorización
  async processOAuthCallback(code: string): Promise<boolean> {
    if (!code) {
      this.isLoadingSubject.next(false);
      this.isAuthenticatedSubject.next(false);
      return false;
    }

    // Activar loading mientras se procesa
    this.isLoadingSubject.next(true);

    // Intercambiar el code por tokens y esperar resultado
    return await this.exchangeCodeForTokens(code);
  }

  // Metodo para cerrar sesion
  async logOut() {
    try {
      // Preservar el mensaje de logout si existe
      const logoutMessage = sessionStorage.getItem('logout_message');

      // Limpiar permisos
      this.userPermissionsService.clearPermissions();

      // Limpiar información del usuario
      this.userInfoService.clearUserInfo();

      // Limpiar tokens y storage
      localStorage.removeItem('id_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.clear();
      sessionStorage.clear();

      // Restaurar el mensaje de logout después de limpiar
      if (logoutMessage) {
        sessionStorage.setItem('logout_message', logoutMessage);
      }

      // Cerrar sesión de Cognito (nota: esto NO cierra la sesión de Google, 
      // pero prompt=select_account en loginWithGoogle forzará selección de cuenta)
      const logoutUrl = `${environment.cognitoUserPoolDomain}/logout?client_id=${environment.cognitoAppClientId}&logout_uri=${encodeURIComponent(environment.cognitoRedirectUri)}`;
      window.location.href = logoutUrl;

    } catch (error) {
      console.error('Error durante el logout:', error);
    }
  }

  // Login con Google mediante Cognito
  // Usa prompt=select_account para forzar que Google muestre el selector de cuentas
  async loginWithGoogle() {
    const nonce = this.generateNonce();
    const state = this.generateNonce();

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: environment.cognitoAppClientId,
      redirect_uri: environment.cognitoRedirectUri,
      scope: 'openid email profile',
      state,
      nonce,
      identity_provider: 'Google',
      prompt: 'select_account'
    });

    sessionStorage.setItem('oauth_state', state);

    window.location.href =
      `${environment.cognitoUserPoolDomain}/oauth2/authorize?${params.toString()}`;
  }

  // Generar string aleatorio para nonce/state
  private generateNonce(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }


}

